import { IssueFragment, IssueFragmentQuery } from '../../utils/gqlModels';
import {
  AddIncorrectTimeNoteFunction,
  checkIssueIncorrectTimeNotes,
  issueMapFunction,
} from '../../utils/issueUtils';
import {
  Issue,
  NON_VALIDATE_PAGE_SIZE,
  Project,
  VALIDATE_PAGE_SIZE,
} from '../../utils/model';
import { graphqlQuery } from './_graphqlQuery';

const getGroupIssuesQuery = (validateNotes: boolean) => `
${IssueFragmentQuery(validateNotes)}
query issues($pid: ID!, $first: Int, $after: String, $updatedAfter: Time, $createdBefore:Time) {
  group(fullPath: $pid) {
    issues(
      first: $first
      sort: UPDATED_DESC
      after: $after
      includeSubgroups: true
      state: all
      updatedAfter: $updatedAfter
       createdBefore: $createdBefore
    ) {
      pageInfo {
        endCursor
      }
      count
      nodes {
        ...IssueFragment
      }
    }
  }
}
`;

interface GetGroupIssueGqlResponse {
  group: {
    issues: {
      pageInfo: {
        endCursor: string;
      };
      count: number;
      nodes: IssueFragment[];
    };
  };
}

const getGroupIssuesGqlPaged = async ({
  projects,
  groupPath,
  endCursor,
  validateNotes,
  from,
  to,
  addIncorrectTimeNote,
}: {
  projects: Project[];
  groupPath: string;
  endCursor: string | undefined;
  addIncorrectTimeNote: AddIncorrectTimeNoteFunction;
  from: Date;
  to: Date;
  validateNotes: boolean;
}): Promise<{ issues: Issue[]; endCursor: string; total: number }> => {
  const data = await graphqlQuery<GetGroupIssueGqlResponse>(
    getGroupIssuesQuery(validateNotes),
    {
      pid: groupPath,
      first: validateNotes ? VALIDATE_PAGE_SIZE : NON_VALIDATE_PAGE_SIZE,
      after: endCursor,
      updatedAfter: from.toISOString(),
      createdBefore: to.toISOString(),
    },
  );

  if (!data.group) {
    return { issues: [], endCursor: '', total: 0 };
  }
  const issues = data.group.issues.nodes.map<Issue | undefined>((issue) => {
    const project = projects.find((p) => p.id === Number(issue.projectId));
    if (!project) return undefined;
    checkIssueIncorrectTimeNotes({
      addIncorrectTimeNote,
      issues: [issue],
      from,
      to,
      project,
      projects,
    });
    return issueMapFunction({ project, projects })(issue);
  });
  return {
    issues: issues.filter((i) => !!i) as Issue[],
    endCursor: data.group.issues.pageInfo.endCursor,
    total: data.group.issues.count,
  };
};

export const getGroupIssuesGql = async ({
  projects,
  groupPath,
  validateNotes,
  from,
  to,
  setProgress,
  addIncorrectTimeNote,
}: {
  addIncorrectTimeNote: AddIncorrectTimeNoteFunction;
  projects: Project[];
  groupPath: string;
  from: Date;
  to: Date;
  setProgress: (progress: string) => void;
  validateNotes: boolean;
}): Promise<Issue[]> => {
  let endCursor: string | undefined;
  const issues: Issue[] = [];
  setProgress('0 / ?');
  let curLoaded = 0;
  do {
    const {
      issues: newIssues,
      endCursor: newEndCursor,
      total,
    } = await getGroupIssuesGqlPaged({
      projects,
      validateNotes,
      groupPath,
      endCursor,
      from,
      to,
      addIncorrectTimeNote,
    });
    curLoaded += newIssues.length;
    setProgress(`${curLoaded} / ${total}`);
    issues.push(...newIssues);
    endCursor = newEndCursor;
  } while (endCursor);
  return issues;
};
